import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";

import { PersistGate } from "redux-persist/lib/integration/react";
import GTMService from "common/services/GTMService";
import configService from "common/services/configService";
import freshChatService from "common/services/freshChat";
import googlePlacesService from "common/services/googlePlaces";
import paymentGatewayService from "common/services/paymentGateways";
import upsdkService from "common/services/upsdkService";
import { persistor, store } from "common/store";
import configSelector from "common/store/selectors/configSelector";
import App from "./app";
import GenericError from "./common/something-went-wrong/index.component";
import { setDefaults } from "./helpers/helmetHelper";
import i18n from "./i18n/index";
import reportWebVitals from "./reportWebVitals";
import LanguageHelper from "common/helpers/language.helper";
import loadPolyfills from "./helpers/polyfills";
import dayjs from "dayjs";
import {
  ORDERING_TYPE_ONLINE,
  ORDERING_TYPE_QR,
} from "./constants/enums.constants";

import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

import "./styles/index.scss";

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: "https://65b81bf9464d490690151ae2f6087de7@o16291.ingest.sentry.io/6268340",
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    environment: "production",
    ignoreErrors: ["nonpromise_rejections", /^Non-Error promise rejection/],
  });
}

let orderingType = ORDERING_TYPE_ONLINE;

if (window) {
  if (window.ROOT_CONFIG.biz?.mode === ORDERING_TYPE_QR) {
    orderingType = ORDERING_TYPE_QR;
  }
}

let SDK_LOADED = false;
let events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];

function initiateSDKS() {
  if (SDK_LOADED) {
    return;
  }

  SDK_LOADED = true;

  events.forEach(function (name) {
    document.removeEventListener(name, initiateSDKS, true);
  });

  let config = window.ROOT_CONFIG;
  let mapsKey = configSelector.getGoogleMapsKey({ config: config });
  const freshChat = configSelector.getFreshChat({ config: config });
  const secondayColor = configSelector.getSecondaryColor({ config: config });
  const gtm = configSelector.getGTM({ config: config });

  googlePlacesService.init({
    apiKey: mapsKey,
    callback: () => {
      return;
    },
  });

  if (freshChat.enable && freshChat.token.length) {
    freshChatService.init({
      token: freshChat.token,
      backgroundColor: secondayColor,
    });
  }

  if (gtm.enable && gtm.id.length) {
    GTMService.init({ gtmId: gtm.id });
  }

  //TODO: need to move to upsdk
  paymentGatewayService.initRazorPay({
    name: window.ROOT_CONFIG.biz.name,
    // image: window.ROOT_CONFIG.biz.logoUrl,
    // theme: { color: primaryColor },
  });
}

async function fireWhenConfigIsReady() {
  if (typeof window.ROOT_CONFIG != "undefined") {
    await loadPolyfills();
    configService.init(window.ROOT_CONFIG);

    if (orderingType === ORDERING_TYPE_ONLINE) {
      upsdkService.init({
        BIZ_ID: window.ROOT_CONFIG.biz.id,
        USERNAME: window.ROOT_CONFIG.biz.username,
        API_KEY: window.ROOT_CONFIG.biz.apiKey,
        ENV: window.ROOT_CONFIG.biz.env || "production",
      });

      window.addEventListener("load", () => {
        events.forEach(function (name) {
          document.addEventListener(name, initiateSDKS, true);
        });
      });
    }

    // set favicon
    const favicon = configSelector.getFavicon({ config: window.ROOT_CONFIG });
    setDefaults({ favicon });
    /**
     * Initialize moment with active language
     */
    const activeLanguage = LanguageHelper.getLanguage(window.ROOT_CONFIG);
    dayjs.locale(activeLanguage);
  }
}

(async () => {
  await fireWhenConfigIsReady();

  ReactDOM.render(
    <Sentry.ErrorBoundary fallback={GenericError()}>
      <Suspense fallback="">
        <I18nextProvider i18n={i18n}>
          <Provider store={store}>
            <PersistGate persistor={persistor}>
              <App orderingType={orderingType} />
            </PersistGate>
          </Provider>
        </I18nextProvider>
      </Suspense>
    </Sentry.ErrorBoundary>,
    document.getElementById("root")
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
})();
